// PostHog events for the Live Board.
//
// Mirrors src/utils/mockAnalytics.js: the event name and its property shape
// live in one place, posthog-js is imported once, and no PII ever lands in
// properties. PostHog itself is initialized in src/App.js.
//
// 🔴 NOTHING FIRES ON A PICK. A draft is 160 of them, and counting them tells
// us nothing the board itself does not — that rule is unchanged and is the
// reason this file has three events rather than one per sale.
//
// It was ONE event until aug28. That was too few, and the way we found out is
// worth writing down: a real superflex auction ran on the board for 1h43m and
// the only thing analytics could say about it was that a board had been
// created. Whether the room finished, stalled at pick 40, or was set up a week
// early for a draft that had not happened yet were all the same shape in the
// data, and the only way to tell them apart was to sit and watch a session
// replay. So `completed` and `halfway` were added — two more events per board
// AT MOST, each fired once, on a state TRANSITION rather than on a write.
//
// ⚠️ THE TRANSITION IS THE GUARD. Both helpers are called from commitBoard,
// which holds `prev` and `next`; the caller fires only when the threshold was
// false for `prev` and true for `next`. There is no ref, no counter and no
// "already sent" flag to get out of sync with a resumed board — a board that
// reloads mid-draft cannot re-announce a milestone it crossed yesterday,
// because crossing it again would require the sale count to fall first.
//
// 🔴 NOTHING HERE MAY CARRY A NAME. The league name and the team names are
// real people typed by the user; they are the reason the setup screen has a
// disclosure block. They stay out of analytics entirely.

import posthog from "posthog-js";

// Analytics must never break the board — swallow capture failures, the same
// rule the entry path follows for the network.
function safeCapture(event, properties) {
  try {
    posthog.capture(event, properties);
  } catch {
    /* ignore */
  }
}

/**
 * Fired once, when a board is created (useLiveBoard's startBoard, on a write
 * that stuck). Not on resume, not on overwrite-blocked, not on a pick.
 *
 * `isSflex` is the LITERAL superflex-slot test, matching `is_sflex` in
 * mock_draft_started — the shipped meaning of that field name (aug24). If the
 * 1QB/2QB/SFLEX split is ever wanted here, it arrives as its own `format`
 * property from `leagueFormatLabel`, never by redefining this one.
 */
export function captureLiveBoardStarted({ teams, budget, rosterSize, isSflex }) {
  safeCapture("live_board_started", {
    teams: teams ?? null,
    budget: budget ?? null,
    roster_size: rosterSize ?? null,
    is_sflex: !!isSflex,
  });
}

/**
 * Fired ONCE, when the last open roster slot on the board fills — the
 * false→true transition of `boardIsComplete`. This is the event that makes
 * "did anyone actually run a draft on this" a number instead of an inference.
 *
 * `sales` and `duration_min` are the two that carry the answer: a 12-team room
 * that finishes has ~160 sales and takes over an hour, and a board someone
 * clicked through to see what it looked like does not.
 *
 * ⚠️ duration_min is wall-clock from board creation, NOT time spent drafting.
 * A board set up on Tuesday for a Sunday auction reads as ~7000 minutes. That
 * is honest — it is the number we have — but it is a floor on engagement and
 * must never be reported as "how long the draft took" without the median doing
 * the work of throwing out the setup-early cases.
 */
export function captureLiveBoardCompleted({ teams, budget, rosterSize, isSflex, sales, startedAt }) {
  safeCapture("live_board_completed", {
    teams: teams ?? null,
    budget: budget ?? null,
    roster_size: rosterSize ?? null,
    is_sflex: !!isSflex,
    sales: Number.isFinite(sales) ? sales : null,
    duration_min: Number.isFinite(startedAt)
      ? Math.round((Date.now() - startedAt) / 60000)
      : null,
  });
}

/**
 * Fired ONCE, when half the board's roster slots have filled.
 *
 * 🔴 THIS EXISTS TO SEPARATE TWO THINGS COMPLETION ALONE CANNOT TELL APART: a
 * room that gave up on us, and a room whose draft is next week. Started-minus-
 * completed is a single number that contains both, and they mean opposite
 * things — one is a bug report, the other is a pipeline. A board that reaches
 * halfway and stops was a real auction that stopped being worth typing into,
 * and that is the only signal on this surface that would ever tell us so.
 */
export function captureLiveBoardHalfway({ teams, budget, rosterSize, isSflex, sales, startedAt }) {
  safeCapture("live_board_halfway", {
    teams: teams ?? null,
    budget: budget ?? null,
    roster_size: rosterSize ?? null,
    is_sflex: !!isSflex,
    sales: Number.isFinite(sales) ? sales : null,
    duration_min: Number.isFinite(startedAt)
      ? Math.round((Date.now() - startedAt) / 60000)
      : null,
  });
}
